import { useRecoilState, useRecoilValue } from 'recoil';
import { Select } from '../Select/Select';
import { selectedSellerNumberState, userAccountsState } from '@sd/shared';
import { Form } from 'react-bootstrap';
import { sortBy, prop } from 'ramda';
import { FC, memo } from 'react';

type Props = {
  readonly?: boolean;
  forcedAccountNumber?: string | number;
};

/**
 * Selects a single seller number
 */
export const SellerNumberSelector: FC<Props> = memo(props => {
  const [selectedSellerNumber, setSelectedSellerNumber] = useRecoilState(selectedSellerNumberState);
  const accounts = useRecoilValue(userAccountsState);

  const sellerNumber = Number(props.forcedAccountNumber || selectedSellerNumber) || undefined;
  if (props.readonly) {
    const account = sellerNumber && accounts.find(ac => ac.sellerNumbers.includes(sellerNumber));
    return (
      <Form.Group>
        <Form.Label htmlFor="account">Account</Form.Label>
        <Form.Control
          id="account"
          readOnly
          value={account ? `${sellerNumber} - ${account.name || 'Other'}` : sellerNumber || 'No account selected'}
        />
      </Form.Group>
    );
  }

  return (
    <Form.Group>
      <Form.Label htmlFor="account">Account</Form.Label>
      <Select
        id="account"
        readOnly={props.readonly}
        options={sortBy(
          prop('label'),
          accounts
            .flatMap(
              account =>
                account.sellerNumbers
                  .filter(sn => sn >= 4000000 && sn <= 5999999)
                  .map(sn => ({
                    label: `${sn} - ${account.name}`,
                    value: sn
                  }))
              // For whatever reason, Vicki returns us multiple account GUIDs for the same account name, but with different
              // lists of seller numbers in each so we dedup them here
            )
            .filter(
              // Dedup
              (item, index, list) => !list.slice(index + 1).some(i => i.label === item.label && i.value === item.value)
            )
        )}
        value={typeof sellerNumber === 'number' ? sellerNumber : undefined}
        onChange={v => setSelectedSellerNumber(v)}
      />
    </Form.Group>
  );
});
